/**
 * Minimal ZIP reader.
 *
 * Walks the central directory and inflates entries with the platform's
 * `DecompressionStream`, so KMZ and zipped Shapefiles are both readable without
 * pulling in a zip library.
 *
 * Only the two methods archives actually use are handled: stored (0) and
 * deflate (8).
 */

const EOCD_SIGNATURE = 0x06054b50;
const CENTRAL_SIGNATURE = 0x02014b50;

interface ZipEntry {
  name: string;
  method: number;
  compressedSize: number;
  localOffset: number;
}

/** Lists the entries in a ZIP without inflating them. */
function readCentralDirectory(buffer: ArrayBuffer): ZipEntry[] {
  const view = new DataView(buffer);
  const bytes = new Uint8Array(buffer);

  // The end-of-central-directory record sits in the last 64 KB (comment aside).
  let eocd = -1;
  const scanFrom = Math.max(0, bytes.length - 66_000);
  for (let offset = bytes.length - 22; offset >= scanFrom; offset -= 1) {
    if (view.getUint32(offset, true) === EOCD_SIGNATURE) {
      eocd = offset;
      break;
    }
  }
  if (eocd < 0) throw new Error('This file is not a readable ZIP archive.');

  const count = view.getUint16(eocd + 10, true);
  let pointer = view.getUint32(eocd + 16, true);
  const entries: ZipEntry[] = [];

  for (let index = 0; index < count; index += 1) {
    if (pointer + 46 > bytes.length || view.getUint32(pointer, true) !== CENTRAL_SIGNATURE) break;

    const method = view.getUint16(pointer + 10, true);
    const compressedSize = view.getUint32(pointer + 20, true);
    const nameLength = view.getUint16(pointer + 28, true);
    const extraLength = view.getUint16(pointer + 30, true);
    const commentLength = view.getUint16(pointer + 32, true);
    const localOffset = view.getUint32(pointer + 42, true);
    const name = new TextDecoder().decode(bytes.subarray(pointer + 46, pointer + 46 + nameLength));

    entries.push({ name, method, compressedSize, localOffset });
    pointer += 46 + nameLength + extraLength + commentLength;
  }

  return entries;
}

async function inflate(entry: ZipEntry, buffer: ArrayBuffer): Promise<Uint8Array> {
  const view = new DataView(buffer);
  const bytes = new Uint8Array(buffer);

  // The payload sits after the local header's own name/extra fields, whose
  // lengths can differ from the central directory's.
  const nameLength = view.getUint16(entry.localOffset + 26, true);
  const extraLength = view.getUint16(entry.localOffset + 28, true);
  const start = entry.localOffset + 30 + nameLength + extraLength;
  const payload = bytes.subarray(start, start + entry.compressedSize);

  if (entry.method === 0) return payload;
  if (entry.method !== 8) {
    throw new Error(`Unsupported ZIP compression method (${entry.method}) for "${entry.name}".`);
  }
  if (typeof DecompressionStream === 'undefined') {
    throw new Error('This browser cannot unzip archives. Extract the files and add them directly.');
  }

  // `deflate-raw`: ZIP stores the deflate stream without a zlib header.
  const stream = new Blob([payload]).stream().pipeThrough(new DecompressionStream('deflate-raw'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

/**
 * Entry names only, read without loading the archive into memory.
 *
 * WHY THIS EXISTS SEPARATELY FROM `readZip`
 * A zipped shapefile is a few megabytes and a zipped File Geodatabase is
 * routinely a few hundred, and the only way to tell them apart is to look at
 * the names inside. Calling `file.arrayBuffer()` to do that would put the
 * whole geodatabase in the tab's memory before we had even decided it was one
 * — for a file that is then going to be uploaded to the server anyway, and
 * never parsed here at all.
 *
 * A ZIP is designed for exactly this read: the end-of-central-directory record
 * is in the last few bytes and points at the file list, so two small ranged
 * reads answer the question regardless of how large the archive is.
 *
 * Returns an empty list rather than throwing when the archive cannot be
 * indexed cheaply (ZIP64, a corrupt tail): the caller falls back to its normal
 * path, which reports a real error against real bytes.
 */
export async function peekZipEntryNames(file: Blob): Promise<string[]> {
  try {
    const tailLength = Math.min(file.size, 66_000);
    if (tailLength < 22) return [];

    const tail = new Uint8Array(await file.slice(file.size - tailLength).arrayBuffer());
    const tailView = new DataView(tail.buffer, tail.byteOffset, tail.byteLength);

    let eocd = -1;
    for (let offset = tail.length - 22; offset >= 0; offset -= 1) {
      if (tailView.getUint32(offset, true) === EOCD_SIGNATURE) {
        eocd = offset;
        break;
      }
    }
    if (eocd < 0) return [];

    const directorySize = tailView.getUint32(eocd + 12, true);
    const directoryOffset = tailView.getUint32(eocd + 16, true);
    // 0xFFFFFFFF is ZIP64's "look in the ZIP64 record instead". Archives that
    // large are past every limit here anyway, so this defers rather than
    // implementing a second format.
    if (directoryOffset === 0xffffffff || directorySize === 0xffffffff) return [];

    const directory = new Uint8Array(
      await file.slice(directoryOffset, directoryOffset + directorySize).arrayBuffer(),
    );
    const view = new DataView(directory.buffer, directory.byteOffset, directory.byteLength);
    const decoder = new TextDecoder();
    const names: string[] = [];

    let pointer = 0;
    while (pointer + 46 <= directory.length) {
      if (view.getUint32(pointer, true) !== CENTRAL_SIGNATURE) break;
      const nameLength = view.getUint16(pointer + 28, true);
      const extraLength = view.getUint16(pointer + 30, true);
      const commentLength = view.getUint16(pointer + 32, true);
      names.push(decoder.decode(directory.subarray(pointer + 46, pointer + 46 + nameLength)));
      pointer += 46 + nameLength + extraLength + commentLength;
    }

    return names;
  } catch {
    return [];
  }
}

/** Inflates every entry, keyed by its path inside the archive. */
export async function readZip(buffer: ArrayBuffer): Promise<Map<string, Uint8Array>> {
  const out = new Map<string, Uint8Array>();
  for (const entry of readCentralDirectory(buffer)) {
    // Directory markers carry no payload.
    if (entry.name.endsWith('/')) continue;
    out.set(entry.name, await inflate(entry, buffer));
  }
  return out;
}

/** Inflates the first entry whose name matches, or undefined. */
export async function readZipEntry(
  buffer: ArrayBuffer,
  match: (name: string) => boolean,
): Promise<Uint8Array | undefined> {
  const entries = readCentralDirectory(buffer).filter((entry) => match(entry.name));
  if (entries.length === 0) return undefined;
  return inflate(entries[0], buffer);
}

/** Standard ZIP/gzip CRC-32 — required on every entry regardless of how (or
 *  whether) its bytes are compressed. */
function crc32(data: Uint8Array): number {
  let crc = ~0;
  for (let index = 0; index < data.length; index += 1) {
    crc ^= data[index];
    for (let bit = 0; bit < 8; bit += 1) {
      crc = (crc >>> 1) ^ (0xedb88320 & -(crc & 1));
    }
  }
  return ~crc >>> 0;
}

export interface ZipFileInput {
  /** Path inside the archive — a plain name for one file, or "folder/name"
   *  for nested entries (see `readZip`'s own path keys for the read side). */
  name: string;
  data: Uint8Array;
}

/**
 * Builds a ZIP archive, STORED (uncompressed).
 *
 * The companion writer to this file's reader, for the same reason the
 * reader needs no library: a browser has `DecompressionStream` to INFLATE a
 * downloaded archive, but no symmetric `CompressionStream` deflate mode —
 * only `gzip`/`deflate`, neither of which is the raw deflate stream a ZIP
 * entry needs. Storing uncompressed avoids that gap entirely and needs
 * nothing beyond a CRC — the only format requirement this skips is
 * compression, never correctness, and every ZIP reader (this app's own
 * included) already handles method 0 for exactly this reason.
 */
export function writeZip(files: ZipFileInput[]): Blob {
  const encoder = new TextEncoder();
  const localParts: Uint8Array[] = [];
  const centralParts: Uint8Array[] = [];
  let offset = 0;

  for (const file of files) {
    const nameBytes = encoder.encode(file.name);
    const crc = crc32(file.data);
    const size = file.data.length;

    const local = new DataView(new ArrayBuffer(30));
    local.setUint32(0, 0x04034b50, true); // local file header signature
    local.setUint16(4, 20, true); // version needed to extract
    local.setUint16(6, 0, true); // general purpose flags
    local.setUint16(8, 0, true); // compression method: 0 = stored
    local.setUint16(10, 0, true); // mod file time
    local.setUint16(12, 0, true); // mod file date
    local.setUint32(14, crc, true);
    local.setUint32(18, size, true); // compressed size
    local.setUint32(22, size, true); // uncompressed size
    local.setUint16(26, nameBytes.length, true);
    local.setUint16(28, 0, true); // extra field length
    localParts.push(new Uint8Array(local.buffer), nameBytes, file.data);

    const central = new DataView(new ArrayBuffer(46));
    central.setUint32(0, 0x02014b50, true); // central directory signature
    central.setUint16(4, 20, true); // version made by
    central.setUint16(6, 20, true); // version needed to extract
    central.setUint16(8, 0, true);
    central.setUint16(10, 0, true);
    central.setUint16(12, 0, true);
    central.setUint16(14, 0, true);
    central.setUint32(16, crc, true);
    central.setUint32(20, size, true);
    central.setUint32(24, size, true);
    central.setUint16(28, nameBytes.length, true);
    central.setUint16(30, 0, true); // extra field length
    central.setUint16(32, 0, true); // file comment length
    central.setUint16(34, 0, true); // disk number start
    central.setUint16(36, 0, true); // internal file attributes
    central.setUint32(38, 0, true); // external file attributes
    central.setUint32(42, offset, true); // offset of local header
    centralParts.push(new Uint8Array(central.buffer), nameBytes);

    offset += 30 + nameBytes.length + size;
  }

  const centralStart = offset;
  const centralSize = centralParts.reduce((sum, part) => sum + part.length, 0);

  const eocd = new DataView(new ArrayBuffer(22));
  eocd.setUint32(0, 0x06054b50, true); // end of central directory signature
  eocd.setUint16(4, 0, true); // disk number
  eocd.setUint16(6, 0, true); // disk with central directory start
  eocd.setUint16(8, files.length, true); // entries on this disk
  eocd.setUint16(10, files.length, true); // entries total
  eocd.setUint32(12, centralSize, true);
  eocd.setUint32(16, centralStart, true);
  eocd.setUint16(20, 0, true); // comment length

  return new Blob([...localParts, ...centralParts, new Uint8Array(eocd.buffer)], {
    type: 'application/zip',
  });
}
